"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import Script from "next/script";
import { GoogleTagManager } from "@next/third-parties/google";
import {
  CONSENT_CHANGE_EVENT,
  clearMarketingCookies,
  getConsent,
  gtmConsentDefaultScript,
  setGtmConsent,
} from "@/lib/consent";

// Inlined at build time (NEXT_PUBLIC_*). Unset in dev/preview → GTM never loads.
const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;

// Loads Google Tag Manager (the ads/marketing layer) ONLY when ALL of:
//   1. the visitor has granted consent, AND
//   2. NEXT_PUBLIC_GTM_ID is configured (prod-only env var), AND
//   3. we are NOT on an authenticated /dashboard page (public pages only).
//
// Mounted once globally in app/layout.tsx, beside GoogleAnalyticsLoader. Reacts
// live to consent changes: Accept loads with no refresh; Decline/withdraw stops
// re-injection and clears the Google Ads cookies. Uses usePathname only (never
// useSearchParams) so it needs no Suspense boundary and cannot reintroduce the
// App Router #310 bug.
//
// Consent Mode v2: an inline script sets the `granted` default for all four
// signals immediately before the container, so the GTM-fired Google tags get
// explicit consent signals (Google's post-2024 EEA expectation). Because the
// container is gated behind consent, the default is always "granted" — we avoid
// the "set default before gtm.js" ordering trap that the cookieless/advanced
// pattern requires. On a live withdrawal we push a `denied` consent update so
// already-loaded tags stop; the container itself isn't torn down until a reload
// (same documented limitation as before), but the consent signal flips at once.
const GoogleTagManagerLoader = () => {
  const pathname = usePathname();
  const [granted, setGranted] = useState(false);

  useEffect(() => {
    setGranted(getConsent() === "granted");

    const onChange = () => {
      const isGranted = getConsent() === "granted";
      setGranted(isGranted);
      if (!isGranted) {
        // Signal withdrawal to any already-loaded container, then clear cookies.
        setGtmConsent("denied");
        clearMarketingCookies();
      }
    };

    window.addEventListener(CONSENT_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_CHANGE_EVENT, onChange);
  }, []);

  if (!GTM_ID) return null;
  if (!granted) return null;
  if (pathname?.startsWith("/dashboard")) return null;

  return (
    <>
      <Script
        id="gtm-consent-default"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: gtmConsentDefaultScript("granted") }}
      />
      <GoogleTagManager gtmId={GTM_ID} />
    </>
  );
};

export default GoogleTagManagerLoader;
